import { priceFormatter } from "~/utils/price-formatter";
import { type Currency } from "~/api/products/types";
import { Choice } from "~/components/kiosk/product-detail/choice";
import { type KioskDisplayVariant } from "~/stores/kiosk-session/hooks/use-kiosk-products/types";

import { type VariantFormData } from "./route";

type VariantFormProps = {
  variant: KioskDisplayVariant;
  variantFormData: VariantFormData | null;
  currency: Currency;
  locale: string;
  hidePrices: boolean;
  onChange: (data: VariantFormData) => void;
};

export const VariantForm = ({
  variant,
  variantFormData,
  currency,
  locale,
  hidePrices,
  onChange
}: VariantFormProps) => {
  const initialFormData = variantFormData || {};

  // If the variant has a minimum, and the form data is empty,
  // pre-populate selecting the first 'variant.minimum' choices
  if (variant.minimum && Object.keys(initialFormData).length === 0) {
    const newFormData: VariantFormData = {};

    variant.choices.slice(0, variant.minimum).forEach((choice) => {
      newFormData[choice.id] = 1;
    });

    onChange(newFormData);
  }

  const totalQuantity = Object.values(initialFormData).reduce(
    (acc, c) => acc + (c || 0),
    0
  );
  const maxReached =
    variant.maximum !== null && totalQuantity >= variant.maximum;

  const toggleChoice = (id: string) => {
    const existing = initialFormData?.[id] || 0;

    if (existing) {
      onChange({ ...initialFormData, [id]: 0 });
      return;
    }

    if (maxReached) {
      if (variant.maximum === 1) {
        // for single choice options, we can just switch choice
        onChange({ [id]: 1 });
      }
      return;
    }

    onChange({ ...initialFormData, [id]: 1 });
  };

  const updateQuantity = (id: string, change: number) => {
    if (change > 0 && maxReached) return;
    const existing = initialFormData?.[id] || 0;
    onChange({ ...initialFormData, [id]: Math.max(existing + change, 0) });
  };

  return (
    <div className="flex w-full flex-col gap-2">
      {variant.choices.map((choice) => {
        const isSelected = Boolean(initialFormData?.[choice.id]);
        const quantity =
          isSelected && initialFormData?.[choice.id]
            ? initialFormData[choice.id]
            : 1;

        return (
          <Choice
            key={choice.id}
            choice={choice}
            variantMaximum={variant.maximum}
            checked={isSelected}
            onRadioClick={() => {
              toggleChoice(choice.id);
            }}
            quantity={quantity}
            onQuantityIncrease={() => {
              updateQuantity(choice.id, 1);
            }}
            onQuantityDecrease={() => {
              updateQuantity(choice.id, -1);
            }}
            displayPrice={
              choice.unitDisplayPrice && !hidePrices
                ? priceFormatter({
                    value: choice.unitDisplayPrice,
                    currency,
                    locale
                  })
                : undefined
            }
          />
        );
      })}
    </div>
  );
};
